import {
  DEFAULT_TIER_PICK,
  SKILL_TIERS,
  type TierPickState,
} from "@/components/search/globalSearchConstants";
import type { Course } from "@/types";

export type InstrumentId =
  | "piano"
  | "guitar"
  | "bass"
  | "drums"
  | "vocals"
  | "saxophone"
  | "trumpet"
  | "vibraphone";

export const COURSE_INSTRUMENTS: { id: InstrumentId; label: string }[] = [
  { id: "piano", label: "Piano" },
  { id: "guitar", label: "Guitar" },
  { id: "bass", label: "Bass" },
  { id: "drums", label: "Drums" },
  { id: "vocals", label: "Vocals" },
  { id: "saxophone", label: "Saxophone" },
  { id: "trumpet", label: "Trumpet" },
  { id: "vibraphone", label: "Vibraphone" },
];

export const INSTRUMENT_IDS: InstrumentId[] = COURSE_INSTRUMENTS.map((i) => i.id);

export type InstrumentPickState = Record<InstrumentId, boolean>;

export const DEFAULT_INSTRUMENT_PICK: InstrumentPickState = {
  piano: false,
  guitar: false,
  bass: false,
  drums: false,
  vocals: false,
  saxophone: false,
  trumpet: false,
  vibraphone: false,
};

export function instrumentLabel(id: InstrumentId): string {
  return COURSE_INSTRUMENTS.find((i) => i.id === id)?.label ?? id;
}

function courseInstruments(course: Course): string[] {
  return Array.isArray(course.instruments) ? course.instruments : [];
}

/**
 * OR match: a course passes if its `instruments` array contains ANY selected
 * instrument. No selection (or all selected) means every course passes.
 */
export function courseMatchesInstruments(
  course: Course,
  pick: InstrumentPickState,
): boolean {
  const selected = INSTRUMENT_IDS.filter((i) => pick[i]);
  if (selected.length === 0 || selected.length === INSTRUMENT_IDS.length) {
    return true;
  }
  const have = courseInstruments(course);
  return selected.some((i) => have.includes(i));
}

export function courseMatchesSkillTiers(
  course: Course,
  tierPick: TierPickState,
): boolean {
  const selected = SKILL_TIERS.filter((t) => tierPick[t]);
  if (selected.length === 0 || selected.length === SKILL_TIERS.length) {
    return true;
  }
  if (course.skill_level == null) return false;
  return selected.includes(course.skill_level);
}

export function filterCourses(
  courses: Course[],
  instrumentPick: InstrumentPickState,
  tierPick: TierPickState,
): Course[] {
  return courses.filter(
    (c) =>
      courseMatchesInstruments(c, instrumentPick) &&
      courseMatchesSkillTiers(c, tierPick),
  );
}

export { DEFAULT_TIER_PICK };
